import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { RecordingButton } from './RecordingButton';
import { useLanguage } from '../context/LanguageContext';

interface GenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GenerateModal({ isOpen, onClose }: GenerateModalProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const CATEGORIES = [
    { id: 'lucky', label: t('feelingLucky'), icon: '🎲', description: t('randomCategory') },
    { id: 'confidence', label: t('confidence'), icon: '💪', description: t('confidenceDesc') },
    { id: 'resilience', label: t('resilience'), icon: '🌟', description: t('resilienceDesc') },
    { id: 'growth', label: t('growth'), icon: '🌱', description: t('growthDesc') },
    { id: 'gratitude', label: t('gratitude'), icon: '🙏', description: t('gratitudeDesc') },
    { id: 'calm', label: t('calm'), icon: '🧘', description: t('calmDesc') },
    { id: 'success', label: t('success'), icon: '🏆', description: t('successDesc') },
  ];
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [generatedPhrase, setGeneratedPhrase] = useState<{ id: string; content: string; focusArea: string } | null>(null);
  const [showRecording, setShowRecording] = useState(false);
  const [recordingComplete, setRecordingComplete] = useState(false);

  const generateMutation = useMutation({
    mutationFn: (focusArea: string) => api.generatePhrase(focusArea, undefined, true),
    onSuccess: (data) => {
      setGeneratedPhrase({
        id: data.id,
        content: data.content,
        focusArea: data.focusArea
      });
      queryClient.invalidateQueries({ queryKey: ['library'] });
    }
  });

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setGeneratedPhrase(null);
    setShowRecording(false);
    setRecordingComplete(false);

    // If "lucky", pick a random category
    let finalCategory = categoryId;
    if (categoryId === 'lucky') {
      const realCategories = CATEGORIES.filter(c => c.id !== 'lucky');
      finalCategory = realCategories[Math.floor(Math.random() * realCategories.length)].id;
    }

    generateMutation.mutate(finalCategory);
  };

  const handleRecordingComplete = () => {
    setRecordingComplete(true);
    queryClient.invalidateQueries({ queryKey: ['myStreak'] });
    queryClient.invalidateQueries({ queryKey: ['categoryStats'] });
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setGeneratedPhrase(null);
    setShowRecording(false);
    setRecordingComplete(false);
    onClose();
  };

  const getCategoryIcon = (focusArea: string) => {
    return CATEGORIES.find(c => c.id === focusArea)?.icon || '✨';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{t('generateAffirmation')}</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {!generatedPhrase ? (
            <>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                {t('chooseFocusArea')}
              </p>

              {/* Category Grid */}
              <div className="category-grid">
                {CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    disabled={generateMutation.isPending}
                    className={`category-btn ${category.id === 'lucky' ? 'lucky' : ''}`}
                  >
                    <div className="category-btn-content">
                      <span className="category-btn-icon">{category.icon}</span>
                      <div className="category-btn-text">
                        <h3>{category.label}</h3>
                        <p>{category.description}</p>
                      </div>
                    </div>
                    {generateMutation.isPending && selectedCategory === category.id && (
                      <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                        <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                        <span>{t('generating')}</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="generated-phrase">
              {/* Generated Phrase Display */}
              <div className="category-badge">
                <span>{getCategoryIcon(generatedPhrase.focusArea)}</span>
                <span style={{ textTransform: 'capitalize' }}>{t(generatedPhrase.focusArea as any)}</span>
              </div>

              <p className="phrase-text">
                "{generatedPhrase.content}"
              </p>

              {!showRecording && !recordingComplete && (
                <div className="actions">
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    {t('practicePrompt')}
                  </p>
                  <button
                    className="btn btn-primary btn-large"
                    onClick={() => setShowRecording(true)}
                  >
                    🎙️ {t('recordEarnPoints')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleCategorySelect(selectedCategory || 'lucky')}
                    disabled={generateMutation.isPending}
                  >
                    🔄 {t('generateAnother')}
                  </button>
                </div>
              )}

              {showRecording && !recordingComplete && (
                <div style={{ marginTop: '1rem' }}>
                  <RecordingButton
                    phraseId=""
                    customPhraseId={generatedPhrase.id || ''}
                    assignmentId=""
                    onComplete={handleRecordingComplete}
                  />
                </div>
              )}

              {recordingComplete && (
                <div className="actions">
                  <div className="success-message">
                    <div className="icon">🎉</div>
                    <p className="title">{t('earnedPoints')}</p>
                    <p className="subtitle">+1 {t(generatedPhrase.focusArea as any)}</p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleCategorySelect(selectedCategory || 'lucky')}
                    disabled={generateMutation.isPending}
                  >
                    ✨ {t('generateAnother')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleClose}
                  >
                    {t('done')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
